import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import { ITranslationSyncStatus } from "../../../@types/admin/AdminMasterTranslations";
import { TableSorted } from "../../../components/TableSorted";
import { useSyncTranslations } from "../../../hooks/mutations/admin/masterTranslations";
import {
  translationsSyncStatusKey,
  useTranslationsSyncStatus,
} from "../../../hooks/queries/admin/masterTranslations";
import { useModal } from "../../../hooks/useModal";
import { getEnvironment, isProduction } from "../../../utils/environment";
import {
  AdminSelectField,
  ErrorMessage,
  FilterButton,
  FilterGroup,
  FilterLabel,
  FiltersActions,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageInfoTooltip,
  PageTitle,
  PageTitleGroup,
  SearchInput,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import { TranslationKey } from "./AdminTranslations.styles";
import { ConfirmSyncAllModal } from "./ConfirmSyncAllModal";
import { describeSyncResult, SyncKeyDetailModal } from "./SyncKeyDetailModal";
import { keyStatusBadge, keyStatusText, summarizeKeyStatus } from "./syncState";

const SEARCH_FIELDS: (keyof ITranslationSyncStatus)[] = ["key"];

type StatusFilter = "" | "needs-sync" | "in-sync";

/**
 * Translations Sync page (EN-5178), the pull-based replacement for the Retool
 * migrate_GlobalTranslations app.
 *
 * Retool pushed from the master store into all four Global DBs from one page.
 * The portal's auth model binds a session to ONE environment, so the flow is
 * inverted: every environment has this page, and it only pulls from the master
 * store into the environment you are logged into. To publish a key everywhere,
 * sync it once per environment, in that environment's portal.
 */
export function AdminTranslationsSync() {
  document.title = "Translations sync - Admin";

  const { openModal, closeModal } = useModal();
  const environmentName = getEnvironment();

  const { status, isLoading, isError, isNotConfigured } =
    useTranslationsSyncStatus();
  const { syncTranslations, isSyncing } = useSyncTranslations();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");

  const rows = useMemo(
    () =>
      status.filter((s) => {
        if (!statusFilter) return true;
        const { needsSync } = summarizeKeyStatus(s);
        return statusFilter === "needs-sync" ? needsSync : !needsSync;
      }),
    [status, statusFilter]
  );

  const pendingKeys = useMemo(
    () => status.filter((s) => summarizeKeyStatus(s).needsSync),
    [status]
  );

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({ data: rows, searchFields: SEARCH_FIELDS });

  const openKeyDetail = (row: ITranslationSyncStatus) => {
    openModal(
      <SyncKeyDetailModal
        translationKey={row.key}
        usedLocation={row.usedLocation}
        environmentName={environmentName}
        onClose={closeModal}
      />,
      { width: "900px", showCloseButton: true }
    );
  };

  const handleSyncAll = () => {
    if (pendingKeys.length === 0) return;
    openModal(
      <ConfirmSyncAllModal
        keyCount={pendingKeys.length}
        environmentName={environmentName}
        isProductionEnvironment={isProduction()}
        onConfirm={async () => {
          const result = await syncTranslations({
            items: pendingKeys.map((s) => ({
              key: s.key,
              usedLocation: s.usedLocation,
            })),
          });
          return describeSyncResult(result);
        }}
        onClose={closeModal}
      />,
      { width: "520px", showCloseButton: true }
    );
  };

  const columns: ColumnDef<ITranslationSyncStatus, string>[] = useMemo(
    () => [
      {
        accessorKey: "key",
        header: "Key",
        cell: ({ row }) => (
          <TranslationKey title={row.original.key}>
            {row.original.key}
          </TranslationKey>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "usedLocation",
        header: "Used location",
        enableSorting: true,
      },
      {
        id: "languages",
        header: "Languages",
        accessorFn: (s) => String(s.languages.length),
        enableSorting: false,
      },
      {
        id: "status",
        header: `Status in ${environmentName}`,
        accessorFn: (s) => keyStatusText(summarizeKeyStatus(s)),
        cell: ({ row }) => {
          const summary = summarizeKeyStatus(row.original);
          return (
            <StatusBadge status={keyStatusBadge(summary)}>
              {keyStatusText(summary)}
            </StatusBadge>
          );
        },
        enableSorting: true,
      },
    ],
    [environmentName]
  );

  if (isNotConfigured) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>Translations sync</PageTitle>
        </PageHeader>
        <ErrorMessage>
          The master translations database is not configured in this
          environment, so there is nothing to sync from. Ask the team to add the
          TranslationsMaster connection string to this environment&apos;s API.
        </ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitleGroup>
          <PageTitle>Translations sync</PageTitle>
          <PageInfoTooltip title="Translations sync">
            Pulls translations from the master store into the {environmentName}{" "}
            environment — this page can never write anywhere else. To publish a
            key to another environment, open this page in that
            environment&apos;s portal. Click a row to compare its values and
            sync per language.
          </PageInfoTooltip>
        </PageTitleGroup>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by key..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <FilterGroup>
          <FilterLabel htmlFor="sync-status-filter">Status</FilterLabel>
          <AdminSelectField
            id="sync-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="">All keys</option>
            <option value="needs-sync">Needs sync</option>
            <option value="in-sync">In sync</option>
          </AdminSelectField>
        </FilterGroup>

        <FiltersActions>
          <FilterButton
            variant="primary"
            disabled={isSyncing || pendingKeys.length === 0}
            onClick={handleSyncAll}
          >
            {isSyncing
              ? "Syncing..."
              : `Sync all pending (${pendingKeys.length})`}
          </FilterButton>
        </FiltersActions>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the sync status. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[translationsSyncStatusKey]}>
          <TableSorted
            columns={columns}
            data={items}
            emptyStateText={isLoading ? <TableLoadingState /> : "No keys found"}
            onRowClick={openKeyDetail}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: `translations-sync-${environmentName}`,
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PageContainer>
  );
}
